import { useEffect } from 'react';

const VERTEX_SHADER = `
attribute vec2 aPosition;
void main() {
  gl_Position = vec4(aPosition, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER = `
precision highp float;

uniform vec2 uResolution;
uniform float uTime;
uniform vec2 uPointer;
uniform float uPointerForce;
uniform float uRise;
uniform vec4 uMaskRect;
uniform float uAmbient;
uniform sampler2D uMask;

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
    u.y
  );
}

float fbm(vec2 p) {
  float value = 0.0;
  float amplitude = 0.5;
  for (int i = 0; i < 4; i++) {
    value += amplitude * noise(p);
    p = mat2(1.6, 1.2, -1.2, 1.6) * p;
    amplitude *= 0.5;
  }
  return value;
}

float wordmark(vec2 uv) {
  if (uRise <= 0.0) return 0.0;
  vec2 m = (uv - uMaskRect.xy) / uMaskRect.zw;
  if (m.x < 0.0 || m.x > 1.0 || m.y < 0.0 || m.y > 1.0) return 0.0;
  // Averaging four taps hides the 8-bit steps that would otherwise show up as jagged bevels.
  vec2 s = vec2(0.0016, 0.008);
  return (
    texture2D(uMask, m + s * vec2(-1.0, -1.0)).a +
    texture2D(uMask, m + s * vec2(1.0, -1.0)).a +
    texture2D(uMask, m + s * vec2(-1.0, 1.0)).a +
    texture2D(uMask, m + s * vec2(1.0, 1.0)).a
  ) * 0.25;
}

float height(vec2 uv, vec2 warp, float aspect) {
  vec2 p = vec2(uv.x * aspect, uv.y) * 1.5;
  float h = fbm(p + warp * 2.1) * 0.62;

  vec2 toPointer = p - vec2(uPointer.x * aspect, uPointer.y) * 1.5;
  h += uPointerForce * 0.2 * exp(-dot(toPointer, toPointer) * 9.0);

  // The wordmark sits in the height field, so it catches light like the liquid around it.
  h += wordmark(uv) * uRise * 0.17;
  return h;
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  float aspect = uResolution.x / uResolution.y;
  float t = uTime * 0.07;

  vec2 p = vec2(uv.x * aspect, uv.y) * 1.5;
  vec2 warp = vec2(fbm(p + vec2(t, -t * 0.6)), fbm(p + vec2(5.2 - t * 0.8, 1.3 + t)));

  vec2 e = vec2(2.5) / uResolution;
  float h = height(uv, warp, aspect);
  float hx = height(uv + vec2(e.x, 0.0), warp, aspect);
  float hy = height(uv + vec2(0.0, e.y), warp, aspect);

  vec3 normal = normalize(vec3((h - hx) / e.x / aspect, (h - hy) / e.y, 2.0));
  vec3 view = vec3(0.0, 0.0, 1.0);
  vec3 reflected = reflect(-view, normal);

  // A dark studio with one long overhead light gives the metal its hard horizon bands.
  float band = smoothstep(0.1, 0.62, reflected.y) * (1.0 - smoothstep(0.62, 0.96, reflected.y));
  float rim = pow(1.0 - clamp(normal.z, 0.0, 1.0), 2.2);
  float spec = pow(clamp(dot(reflected, normalize(vec3(-0.25, 0.62, 0.74))), 0.0, 1.0), 42.0);

  vec3 abyss = vec3(0.012, 0.018, 0.035);
  vec3 trench = vec3(0.03, 0.06, 0.14);
  vec3 cobalt = vec3(0.1, 0.27, 0.95);
  vec3 ice = vec3(0.72, 0.82, 1.0);

  vec3 color = mix(abyss, trench, smoothstep(0.25, 0.75, h));
  color += cobalt * band * 0.58;
  color += cobalt * rim * 0.22;
  color += ice * spec * 0.95;

  // The letters are a solid body of polished metal, lit from above, so they read at a glance.
  float body = smoothstep(0.42, 0.78, wordmark(uv)) * uRise;
  vec3 metal = mix(vec3(0.05, 0.11, 0.42), vec3(0.2, 0.36, 1.0), smoothstep(0.1, 0.9, warp.x + (uv.y - 0.5) * 1.1));
  color = mix(color, metal + cobalt * band * 0.4 + ice * spec, body);

  // Fade into the page at the bottom, where the headline sits.
  // Phones stack more text over the liquid, so the fade climbs higher.
  float fadeEnd = aspect < 0.8 ? 0.66 : 0.5;
  float heroFade = mix(aspect < 0.8 ? 0.05 : 0.1, 1.0, smoothstep(fadeEnd - 0.44, fadeEnd, uv.y));

  // As a background behind other content, the liquid stays evenly dim.
  color *= mix(heroFade, 0.42, uAmbient);
  gl_FragColor = vec4(color, 1.0);
}
`;

const compile = (gl, type, source) => {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(shader));
    return null;
  }
  return shader;
};

// In the logo file the "o" and the dot of the "i" fade out to the right, which reads as a "c" in a
// height field. Mirroring their left halves makes them solid. Positions are fractions of the logo width.
const FADING_SHAPES = [
  { start: 0, end: 0.0642 },
  { start: 0.825, end: 0.8475 },
];

const solidifyLogo = (image) => {
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, 0, 0);
  FADING_SHAPES.forEach(({ start, end }) => {
    const sx = start * image.width;
    const half = (end - start) * image.width;
    ctx.save();
    ctx.translate((end * image.width) * 2, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(image, sx, 0, half, image.height, sx, 0, half, image.height);
    ctx.restore();
  });
  return canvas;
};

// Two blur radii give the letters a soft shoulder and a crisp top, like a pressed shape.
const buildMask = (image) => {
  const logo = solidifyLogo(image);
  const width = 1340;
  const height = Math.round(width * (image.height / image.width));
  const pad = 48;
  const canvas = document.createElement('canvas');
  canvas.width = width + pad * 2;
  canvas.height = height + pad * 2;
  const ctx = canvas.getContext('2d');
  ctx.filter = 'blur(14px)';
  ctx.globalAlpha = 0.55;
  ctx.drawImage(logo, pad, pad, width, height);
  ctx.filter = 'blur(4px)';
  ctx.globalAlpha = 0.6;
  ctx.drawImage(logo, pad, pad, width, height);
  return { canvas, pad, width, height };
};

// With no maskSrc the field is plain liquid. "ambient" dims it for use behind content, and
// "stillOnSmall" draws a single frame on phones and tablets so only the hero animates there.
export function useLiquidField(canvasRef, { maskSrc, ambient = false, stillOnSmall = false } = {}) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'high-performance' });
    if (!gl) return undefined;

    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vertex || !fragment) return undefined;

    const program = gl.createProgram();
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const uniform = (name) => gl.getUniformLocation(program, name);
    const uniforms = {
      resolution: uniform('uResolution'),
      time: uniform('uTime'),
      pointer: uniform('uPointer'),
      pointerForce: uniform('uPointerForce'),
      rise: uniform('uRise'),
      maskRect: uniform('uMaskRect'),
    };
    gl.uniform1f(uniform('uAmbient'), ambient ? 1 : 0);

    const isStill = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      || (stillOnSmall && window.matchMedia('(max-width: 960px), (pointer: coarse)').matches);
    const state = {
      mask: null,
      frame: 0,
      onScreen: true,
      start: performance.now(),
      pointer: [0.5, 0.6],
      target: [0.5, 0.6],
      force: 0,
      targetForce: 0,
    };

    const placeMask = () => {
      if (!state.mask) return;
      const { pad, width, height } = state.mask;
      const rect = canvas.getBoundingClientRect();
      const isNarrow = rect.width < 720;
      const markWidth = isNarrow ? 0.9 : 0.86;
      const markHeight = (markWidth * rect.width * (height / width)) / rect.height;
      const scaleX = (width + pad * 2) / width;
      const scaleY = (height + pad * 2) / height;
      const w = markWidth * scaleX;
      const h = markHeight * scaleY;
      const top = isNarrow ? 0.76 : 0.68;
      gl.uniform4f(uniforms.maskRect, 0.5 - w / 2, top - h / 2, w, h);
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const scale = Math.min(window.devicePixelRatio || 1, 1.5) * (rect.width > 1600 ? 0.7 : 0.85);
      canvas.width = Math.max(1, Math.round(rect.width * scale));
      canvas.height = Math.max(1, Math.round(rect.height * scale));
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uniforms.resolution, canvas.width, canvas.height);
      placeMask();
      // Resizing clears the canvas, so a still frame has to be drawn again.
      if (isStill) draw(performance.now());
    };

    const draw = (now) => {
      const elapsed = (now - state.start) / 1000;
      state.pointer[0] += (state.target[0] - state.pointer[0]) * 0.06;
      state.pointer[1] += (state.target[1] - state.pointer[1]) * 0.06;
      state.force += (state.targetForce - state.force) * 0.05;

      // The wordmark surfaces once on load. This is the page's one orchestrated moment.
      const progress = Math.min(Math.max((elapsed - 0.25) / 2.2, 0), 1);
      const rise = isStill ? 1 : 1 - Math.pow(1 - progress, 3);

      gl.uniform1f(uniforms.time, isStill ? 12 : elapsed + 12);
      gl.uniform2f(uniforms.pointer, state.pointer[0], state.pointer[1]);
      gl.uniform1f(uniforms.pointerForce, state.force);
      gl.uniform1f(uniforms.rise, state.mask ? rise : 0);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    const shouldAnimate = () => state.onScreen && !document.hidden;

    const loop = (now) => {
      draw(now);
      state.frame = shouldAnimate() ? requestAnimationFrame(loop) : 0;
    };

    const startLoop = () => {
      if (isStill) {
        draw(performance.now());
      } else if (!state.frame && shouldAnimate()) {
        state.frame = requestAnimationFrame(loop);
      }
    };

    const onPointerMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      state.target = [(event.clientX - rect.left) / rect.width, 1 - (event.clientY - rect.top) / rect.height];
      state.targetForce = 1;
    };
    const onPointerLeave = () => {
      state.targetForce = 0;
    };

    const image = new Image();
    image.onload = () => {
      const mask = buildMask(image);
      const texture = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, mask.canvas);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      state.mask = mask;
      state.start = performance.now();
      placeMask();
      if (isStill) draw(performance.now());
    };
    if (maskSrc) image.src = maskSrc;

    // Stop drawing when the canvas is off screen or the tab is hidden, to save battery.
    const observer = new IntersectionObserver(([entry]) => {
      state.onScreen = entry.isIntersecting;
      startLoop();
    });
    observer.observe(canvas);

    const onVisibility = () => startLoop();

    resize();
    startLoop();
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.documentElement.addEventListener('pointerleave', onPointerLeave);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelAnimationFrame(state.frame);
      image.onload = null;
      observer.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointerMove);
      document.documentElement.removeEventListener('pointerleave', onPointerLeave);
      document.removeEventListener('visibilitychange', onVisibility);
      gl.deleteProgram(program);
    };
  }, [canvasRef, maskSrc, ambient, stillOnSmall]);
}
